"use client";

import posthog, { type PostHog } from "posthog-js";
import { PostHogProvider as PostHogProviderRaw } from "posthog-js/react";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { keys } from "../../keys";
import { useConsent } from "../hooks/use-consent";

type ConsentAwarePostHogProviderProps = {
  readonly children: ReactNode;
};

export const ConsentAwarePostHogProvider = ({
  children
}: ConsentAwarePostHogProviderProps) => {
  const { hasConsentFor } = useConsent();
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const hasMeasurementConsent = hasConsentFor("measurement");

    if (!isInitialized) {
      // Initialize PostHog with appropriate persistence based on consent
      posthog.init(keys().NEXT_PUBLIC_POSTHOG_KEY, {
        api_host: "/ingest",
        capture_pageleave: true,
        capture_pageview: false,
        opt_out_capturing_by_default: !hasMeasurementConsent,
        persistence: hasMeasurementConsent ? "localStorage+cookie" : "memory",
        person_profiles: "identified_only",
        ui_host: keys().NEXT_PUBLIC_POSTHOG_HOST
      }) as PostHog;
      setIsInitialized(true);
    } else {
      // Update existing PostHog instance based on consent changes
      if (hasMeasurementConsent) {
        posthog.set_config({
          persistence: "localStorage+cookie"
        });
        posthog.opt_in_capturing();
      } else {
        // Clear any existing cookies/localStorage
        posthog.clear_opt_in_out_capturing();
        posthog.set_config({
          persistence: "memory"
        });
        posthog.opt_out_capturing();
      }
    }
  }, [hasConsentFor, isInitialized]);

  return <PostHogProviderRaw client={posthog}>{children}</PostHogProviderRaw>;
};

export { usePostHog as useAnalytics } from "posthog-js/react";
